<template>
  <lb-dialog
    :value="visible"
    :heading="dialogTitle"
    width="512"
    :loading="saving"
    :data-testid="`tasklist__row-edit-${assignType}-dialog`"
    @input="$emit('update:visible', $event)"
  >
    <template v-slot:body>
      <UserChipMultiSelect
        v-model="selectedUserIds"
        :users="locationScopedUsers"
        :loading="usersFetchLoading"
        variant="assignment-dialog"
        :label="searchLabel"
        :current-users-label="currentUsersLabel"
        :role-label="assignType"
        placeholder="Find By Name Or Email..."
        :data-test-id="`tasklist__row-edit-${assignType}-users`"
      />
      <p v-if="errorMessage" class="row-user-assignment-dialog__error">{{ errorMessage }}</p>
    </template>

    <template v-slot:actions>
      <div class="d-flex justify-end gap-2">
        <base-button
          label="Cancel"
          secondary
          :disabled="saving"
          :data-testid="`tasklist__row-edit-${assignType}-cancel`"
          @click="close"
        />
        <base-button
          label="Save"
          primary
          :loading="saving"
          :disabled="saving"
          :data-testid="`tasklist__row-edit-${assignType}-save`"
          @click="save"
        />
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import './RowUserAssignmentDialog.scss';
import { UserChipMultiSelect } from '../../../../../components';
import { TASKLIST_USER_TYPES } from '../../../../../utils/listConstants';
import { USER_ROLE, APPROVER_TAG } from '../../../../../utils/constants';
import {
  fetchUsersForLocations,
  filterUsersToAllowedIds,
  dropIdsNotAllowed,
} from '../../../../../utils/locationUserAccess';
import {
  activeUserIdsFromUserRoles,
  normalizeUserIdList,
} from '../../../../Setup/TaskList/utils/assignmentRoles';

const ASSIGNMENT_TYPES = [TASKLIST_USER_TYPES.PREPARER, TASKLIST_USER_TYPES.APPROVER];

/** Same resolution as TasklistView.assignmentIdsForDisplay (user_roles + L1, else legacy flat). */
function assignmentIdsForDisplay(task, role) {
  if (Array.isArray(task?.user_roles) && task.user_roles.length) {
    const tag = role === USER_ROLE.APPROVER ? APPROVER_TAG.L1 : null;
    return activeUserIdsFromUserRoles(task.user_roles, role, tag);
  }
  if (role === USER_ROLE.PREPARER) return normalizeUserIdList(task?.preparer);
  if (role === USER_ROLE.APPROVER) return normalizeUserIdList(task?.approver);
  return [];
}

export default {
  name: 'RowUserAssignmentDialog',
  components: { UserChipMultiSelect },
  props: {
    visible: { type: Boolean, default: false },
    assignType: {
      type: String,
      default: TASKLIST_USER_TYPES.PREPARER,
      validator: (v) => ASSIGNMENT_TYPES.includes(v),
    },
    task: { type: Object, default: () => ({}) },
    users: { type: Array, default: () => [] },
    cycleId: { type: String, default: '' },
  },
  data() {
    return {
      selectedUserIds: [],
      saving: false,
      errorMessage: '',
      allowedUserIds: [],
      usersFetchLoading: false,
      usersFetchToken: 0,
    };
  },
  computed: {
    dialogTitle() {
      return this.assignType === TASKLIST_USER_TYPES.PREPARER ? 'Edit Preparer' : 'Edit Approver';
    },
    searchLabel() {
      return this.assignType === TASKLIST_USER_TYPES.PREPARER
        ? 'Search And Add Preparer'
        : 'Search And Add Approver';
    },
    currentUsersLabel() {
      return this.assignType === TASKLIST_USER_TYPES.PREPARER ? 'Current Preparers' : 'Current Approvers';
    },
    resolvedCycleId() {
      return this.cycleId || this.task?.project || '';
    },
    locationScopedUsers() {
      const oppositeRole = this.assignType === TASKLIST_USER_TYPES.PREPARER
        ? USER_ROLE.APPROVER
        : USER_ROLE.PREPARER;
      const exclude = new Set(assignmentIdsForDisplay(this.task, oppositeRole).map(String));
      return filterUsersToAllowedIds(this.users, this.allowedUserIds)
        .filter((user) => user?._id && !exclude.has(String(user._id)));
    },
  },
  watch: {
    // Immediate: TasklistView mounts this dialog lazily, already open.
    visible: {
      handler(isOpen) {
        if (isOpen) {
          this.resetForm();
          this.fetchAllowedUsers();
        }
      },
      immediate: true,
    },
    task: {
      handler() {
        if (this.visible) {
          this.resetForm();
          this.fetchAllowedUsers();
        }
      },
      deep: true,
    },
  },
  methods: {
    resetForm() {
      const key = this.assignType;
      this.selectedUserIds = [...(this.task?.[key] || [])];
      this.errorMessage = '';
      this.saving = false;
    },
    async fetchAllowedUsers() {
      const locationId = this.task?.location;
      const locationIds = locationId ? [locationId] : [];
      if (!locationIds.length) {
        this.usersFetchToken += 1;
        this.usersFetchLoading = false;
        this.allowedUserIds = [];
        this.selectedUserIds = dropIdsNotAllowed(this.selectedUserIds, this.allowedUserIds);
        return;
      }
      const token = ++this.usersFetchToken;
      this.usersFetchLoading = true;
      try {
        const ids = await fetchUsersForLocations({
          locationIds,
          cycleId: this.resolvedCycleId,
        });
        if (token !== this.usersFetchToken) return;
        this.allowedUserIds = ids;
        this.selectedUserIds = dropIdsNotAllowed(this.selectedUserIds, ids);
      } catch (err) {
        if (token !== this.usersFetchToken) return;
        console.error('[RowUserAssignmentDialog]: usersForLocations error', err);
      } finally {
        if (token === this.usersFetchToken) this.usersFetchLoading = false;
      }
    },
    close() {
      this.$emit('update:visible', false);
    },
    validateSelection() {
      if (!this.selectedUserIds.length) {
        this.errorMessage = `At least one ${this.assignType} is required.`;
        return false;
      }

      const oppositeRole = this.assignType === TASKLIST_USER_TYPES.PREPARER
        ? USER_ROLE.APPROVER
        : USER_ROLE.PREPARER;
      const opposite = new Set(assignmentIdsForDisplay(this.task, oppositeRole).map(String));
      const overlap = this.selectedUserIds.filter((id) => opposite.has(String(id)));
      if (overlap.length) {
        this.errorMessage = 'A user cannot be both preparer and approver.';
        return false;
      }

      this.errorMessage = '';
      return true;
    },
    async save() {
      if (!this.task?._id || !this.validateSelection()) return;

      const payload = {
        [TASKLIST_USER_TYPES.PREPARER]: [...(this.task[TASKLIST_USER_TYPES.PREPARER] || [])],
        [TASKLIST_USER_TYPES.APPROVER]: [...(this.task[TASKLIST_USER_TYPES.APPROVER] || [])],
      };
      payload[this.assignType] = [...this.selectedUserIds];

      this.saving = true;
      try {
        const result = await this.$store.dispatch('tasks/editChecklistAssignment', {
          taskId: this.task._id,
          checklistId: this.resolvedCycleId,
          [TASKLIST_USER_TYPES.PREPARER]: payload[TASKLIST_USER_TYPES.PREPARER],
          [TASKLIST_USER_TYPES.APPROVER]: payload[TASKLIST_USER_TYPES.APPROVER],
        });

        if (result?.success) {
          this.$store.commit(
            'sbSuccess',
            this.assignType === TASKLIST_USER_TYPES.PREPARER ? 'Preparer updated' : 'Approver updated'
          );
          this.$emit('saved', { task: this.task, assignType: this.assignType });
          this.close();
        } else {
          this.$store.commit('sbError', result?.errorMessage || 'Unable to update assignment');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
